/**
 * One date shape for the whole console, and it is the organization's.
 *
 * ## Why this module exists
 *
 * Founder, 2026-09-16: *"Once this is set, the same date format for the documents should be used
 * everywhere."* Before it, five screens each drew a date their own way — `toLocaleDateString()`
 * with no options in the template list, `{ dateStyle: 'medium' }` in the Inbox, `slice(0, 10)` in
 * two places, `{ day: 'numeric', month: 'short', year: 'numeric' }` on the dashboard — so one
 * organization could see `9/13/2026`, `13 Sep 2026`, `2026-09-13` and `Sep 13, 2026` for the same
 * day, on four screens, in one session. None of them was wrong on its own and together they were
 * a product that could not say what day it was.
 *
 * The shape is the organization's `document_date_format` (ADR 0080, ADR 0132), so a date in the
 * Inbox reads as it will read on the paper.
 *
 * ## The console reads it LIVE, and the ceremony reads the snapshot
 *
 * Two different questions, deliberately answered differently. A list is not evidence: "when did
 * this envelope complete" should read the way the organization reads today, so the console takes
 * the current value from the shell's own `GET /v1/organization`. A document is evidence: it keeps
 * the rule it was sent under, so the ceremony and the flatten read the frozen snapshot
 * (`ceremony_options.date_format`, `delivery_policy_snapshot`). An organization that changes the
 * setting sees its lists change and its sent documents unchanged, which is what ADR 0080 promised.
 *
 * ## Times are not in scope
 *
 * `document_date_format` names a DAY. Where the console shows a moment — an audit entry, a
 * delivery timestamp — that stays `toLocaleString`, in the reader's own locale and zone, because
 * "3:42 pm" is about the reader and "13/09/2026" is about the document.
 */
import { useOutletContext } from 'react-router-dom'
import {
  DEFAULT_DOCUMENT_DATE_FORMAT,
  formatDocumentDate,
  isDocumentDateFormat,
  type DocumentDateFormat,
} from '@flowza/core/fill/date-format'
import type { ShellOutletContext } from './Shell'

/**
 * The organization's shape, from the shell — validated here, because this is where a valid shape
 * is defined and because the shell is on every route's critical path (see `ShellOutletContext`).
 *
 * Falls back to the day-first default in three cases that are one case: no shell context (the admin
 * console mounts some of these screens under its own outlet), the organization has not answered
 * yet, and an older Worker that does not serve the field at all. None is worth a crash and none is
 * worth the browser's own order, which is what this change exists to stop showing.
 */
export function useOrgDateFormat(): DocumentDateFormat {
  const outlet = useOutletContext<Partial<ShellOutletContext> | null>()
  const raw = outlet?.dateFormat
  return isDocumentDateFormat(raw) ? raw : DEFAULT_DOCUMENT_DATE_FORMAT
}

/**
 * An ISO timestamp as a DAY, in the organization's shape.
 *
 * The day is taken in the reader's own zone — `toISOString()` would put a Muscat evening on the
 * previous day for a reader who was there when it happened — and then drawn in the organization's
 * order. A value that is not a date draws as the fallback rather than as `Invalid Date`.
 */
export function formatDay(iso: string | null, format: DocumentDateFormat, fallback = '—'): string {
  if (iso === null || iso === '') return fallback
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return fallback
  const canonical = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(
    at.getDate(),
  ).padStart(2, '0')}`
  return formatDocumentDate(canonical, format)
}
